import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useVirtualBackgroundContext } from 'contexts/VirtualBackgroundContext';
import { useParams } from 'react-router-dom';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { toPng } from 'html-to-image';
import { Typography, Box, CircularProgress, Button } from '@mui/material';
import { DownloadIcon, CheckIcon } from 'icons/materialIcons';
import { useCardContext } from 'contexts/CardContext'; // Import the CardContext
import QRCodeStyling from 'qr-code-styling'; // Import QRCodeStyling directly

const TeamVirtualBackgroundPreview = ({
    contextId,
    containerRef: externalContainerRef,
    hideDownload = false,
    onPreviewReady,
    hideTitle = false
}) => {
    const { backgroundCardState } = useVirtualBackgroundContext();
    const params = useParams();
    const cardId = contextId || params.cardId;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const internalContainerRef = useRef(null);
    const containerRef = externalContainerRef || internalContainerRef;
    const qrContainerRef = useRef(null); // Container for QR code
    const qrRef = useRef(null); // Reference to QRCodeStyling instance
    const { cardState, setLeadCaptureModeOn } = useCardContext(); // Access the Card context

    const leadCaptureModeOn = cardState[cardId]?.leadCaptureModeOn;

    const BASE_URL = `${FRONTEND_BASE_URL}/card`;
    const cardUrl = `${BASE_URL}/${cardId}`;

    useEffect(() => {
        const initializeQR = async () => {
            if (qrContainerRef.current && backgroundCardState[cardId]?.virtualBackgroundContextShowQRCode) {
                // Clear any existing QR code
                qrContainerRef.current.innerHTML = '';

                const qrColor = backgroundCardState[cardId]?.virtualBackgroundContextQRCodeColor || '#000000';
                const logoUrl = backgroundCardState[cardId]?.virtualBackgroundContextQRCodeLogoUrl;

                try {
                    // Slightly reduced base size
                    qrRef.current = new QRCodeStyling({
                        width: 250,   // Reduced from 300
                        height: 250,  // Reduced from 300
                        type: "svg",
                        data: cardUrl,
                        image: logoUrl,
                        dotsOptions: {
                            color: qrColor,
                            type: "square"
                        },
                        cornersSquareOptions: {
                            type: "square"
                        },
                        cornersDotOptions: {
                            type: "square"
                        },
                        backgroundOptions: {
                            color: "#ffffff"
                        },
                        imageOptions: {
                            crossOrigin: "anonymous",
                            margin: 9,
                            imageSize: 0.7,
                            hideBackgroundDots: logoUrl ? true : false,
                        },
                        qrOptions: {
                            errorCorrectionLevel: "H"
                        },
                    });

                    await qrRef.current.append(qrContainerRef.current);

                    // Adjust SVG sizing
                    const svgElement = qrContainerRef.current.querySelector('svg');
                    if (svgElement) {
                        // Slightly reduce size if needed
                        svgElement.setAttribute('width', '70px');   // Reduced from 75px
                        svgElement.setAttribute('height', '70px');  // Reduced from 75px
                        svgElement.setAttribute('viewBox', '0 0 250 250');
                        svgElement.style.width = '70px';
                        svgElement.style.height = '70px';
                    }

                    setLoading(false);
                } catch (err) {
                    console.error('Error initializing QR code:', err);
                    setError('Failed to generate QR Code');
                    setLoading(false);
                }
            }
        };

        initializeQR();
    }, [
        cardUrl,
        backgroundCardState[cardId]?.virtualBackgroundContextQRCodeColor,
        backgroundCardState[cardId]?.virtualBackgroundContextQRCodeLogoUrl,
        backgroundCardState[cardId]?.virtualBackgroundContextShowQRCode
    ]);

    useEffect(() => {
        if (!backgroundCardState[cardId]?.virtualBackgroundContextShowQRCode) {
            setLoading(false);
        }
    }, [backgroundCardState, cardId]);

    useEffect(() => {
        if (!loading && !error && onPreviewReady) {
            onPreviewReady(true);
        }
    }, [loading, error, onPreviewReady]);



    // Log leadCaptureModeOn whenever the component loads or updates
    useEffect(() => {
        console.log("leadCaptureModeOn:", leadCaptureModeOn);
    }, [leadCaptureModeOn]); // Depend on leadCaptureModeOn to log it on change

    const handleDownload = () => {
        if (containerRef.current) {
            const scale = 4;

            toPng(containerRef.current, {
                width: containerRef.current.offsetWidth * scale,
                height: containerRef.current.offsetHeight * scale,
                style: {
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    width: `${containerRef.current.offsetWidth}px`,
                    height: `${containerRef.current.offsetHeight}px`,
                },
                quality: 1.0,
            })
                .then((dataUrl) => {
                    const link = document.createElement('a');
                    link.href = dataUrl;
                    link.download = `virtual-background-${cardId}.png`;
                    link.click();
                })
                .catch((err) => {
                    console.error('Could not generate image:', err);
                });
        }
    };

    return (
        <Box>
            {!hideTitle && (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        fontSize: '12px',
                        color: '#828282',
                        padding: '8px',
                        textAlign: 'center',
                        marginBottom: '28px',
                        marginTop: '16px',
                    }}>
                    Virtual background preview
                </Typography>
            )}

            <Box
                ref={containerRef}
                sx={{
                    width: '100%',
                    height: '200px',
                    borderRadius: '10px',
                    backgroundImage: `url(${backgroundCardState[cardId]?.virtualBackgroundContextBackgroundImageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white',
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'margin 0.2s ease',
                        marginLeft: backgroundCardState[cardId]?.virtualBackgroundContextCondensedView ? '30px' : 0,
                        marginTop: backgroundCardState[cardId]?.virtualBackgroundContextCondensedView ? '12px' : 0,
                    }}>

                    {backgroundCardState[cardId]?.virtualBackgroundContextShowCardName && (
                        <Box
                            sx={{
                                fontSize: '9px',
                                fontWeight: 600,
                                backgroundColor: '#ffffff',
                                color: '#000',
                                padding: '4px',
                                borderRadius: '3px',
                                width: 'fit-content',
                            }}>
                            {backgroundCardState[cardId]?.virtualBackgroundContextCardName?.trim() || 'Name'}
                        </Box>
                    )}

                    {backgroundCardState[cardId]?.virtualBackgroundContextShowCompany &&
                        backgroundCardState[cardId]?.virtualBackgroundContextShowJobTitle && (
                            <Box
                                sx={{
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    backgroundColor: '#ffffff',
                                    color: '#000',
                                    padding: '4px',
                                    borderRadius: '3px',
                                    width: 'fit-content',
                                    marginTop: '3px',
                                }}>
                                {`${backgroundCardState[cardId]?.virtualBackgroundContextJobTitle?.trim() || 'Job Title'} at ${backgroundCardState[cardId]?.virtualBackgroundContextCompany?.trim() || 'Company'}`}
                            </Box>
                        )}

                    {backgroundCardState[cardId]?.virtualBackgroundContextShowCompany &&
                        !backgroundCardState[cardId]?.virtualBackgroundContextShowJobTitle && (
                            <Box
                                sx={{
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    backgroundColor: '#ffffff',
                                    color: '#000',
                                    padding: '4px',
                                    borderRadius: '3px',
                                    width: 'fit-content',
                                    marginTop: '3px',
                                }}>
                                {backgroundCardState[cardId]?.virtualBackgroundContextCompany?.trim() || 'Company'}
                            </Box>
                        )}

                    {backgroundCardState[cardId]?.virtualBackgroundContextShowJobTitle &&
                        !backgroundCardState[cardId]?.virtualBackgroundContextShowCompany && (
                            <Box
                                sx={{
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    backgroundColor: '#ffffff',
                                    color: '#000',
                                    padding: '4px',
                                    borderRadius: '3px',
                                    width: 'fit-content',
                                    marginTop: '3px',
                                }}>
                                {backgroundCardState[cardId]?.virtualBackgroundContextJobTitle?.trim() || 'Job Title'}
                            </Box>
                        )}

                    {backgroundCardState[cardId]?.virtualBackgroundContextShowLocation && (
                        <Box
                            sx={{
                                fontSize: '9px',
                                fontWeight: 600,
                                backgroundColor: '#ffffff',
                                color: '#000',
                                padding: '4px',
                                borderRadius: '3px',
                                width: 'fit-content',
                                marginTop: '3px',
                            }}>
                            {backgroundCardState[cardId]?.virtualBackgroundContextLocation?.trim() || 'Location'}
                        </Box>
                    )}
                </Box>

                {backgroundCardState[cardId]?.virtualBackgroundContextShowQRCode && (
                    <Box
                        sx={{
                            color: '#000',
                            transition: 'margin 0.2s ease',
                            marginRight: backgroundCardState[cardId]?.virtualBackgroundContextCondensedView ? '30px' : 0,
                            marginTop: backgroundCardState[cardId]?.virtualBackgroundContextCondensedView ? '12px' : 0,
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}>
                        <Box
                            sx={{
                                backgroundColor: '#ffffff',
                                borderRadius: '5px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: '75px',
                                padding: 0,
                            }}>


                            <Box
                                sx={{
                                    width: '75px',
                                    height: '75px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}>
                                {/* QR code container with exact dimensions */}
                                <Box
                                    ref={qrContainerRef}
                                    sx={{
                                        width: '75px',
                                        height: '75px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: 0,
                                        margin: 0
                                    }}
                                />
                            </Box>


                            <Typography
                                sx={{
                                    fontSize: '0.5rem',
                                    fontWeight: 600,
                                    color: '#000',
                                    textAlign: 'center',
                                    width: '100%',
                                }}>
                                Connect with me
                            </Typography>
                        </Box>
                        {loading && (
                            <CircularProgress
                                size={20}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)'
                                }}
                            />
                        )}
                        {error && (
                            <Typography variant="body1" color="error">
                                {error}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>

            {!hideDownload && (
                <Box sx={{ textAlign: 'center', marginTop: '16px' }}>
                    <Button
                        onClick={handleDownload}
                        startIcon={<DownloadIcon />}
                        sx={{
                            border: '1px solid #dedede',
                            color: '#4f4f4f',
                            borderRadius: '30px',
                            padding: '8px 16px',
                            textTransform: 'none',
                            marginTop: '18px',
                        }}>
                        Download Background
                    </Button>
                </Box>
            )}

            {leadCaptureModeOn && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '54px' }}>
                    <Typography variant="body1" sx={{ color: '#868282', fontSize: "0.7rem", fontWeight: "600" }}>
                        Lead capture mode is on
                    </Typography>
                    <CheckIcon sx={{ color: '#868282', fontSize: "1.1rem", fontWeight: "700", marginLeft: '4px' }} />
                </Box>
            )}
        </Box>
    );
};

export default TeamVirtualBackgroundPreview;